import React, { useState, useRef, useCallback, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import DateRangePicker from "../../components/DateRangePicker";
import {
  ComposedChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceArea,
  ResponsiveContainer,
  Legend,
} from "recharts";
import html2canvas from "html2canvas";
import Sidebar from "../../components/Sidebar";
import { AppHeader } from "../../components/layout/AppHeader";
import PageContainer from "../../components/PageContainer";
import { DiagnosisAPI, VitalsTrendPoint } from "../../services/diagnosis.service";
import { PatientAPI } from "../../services/patient.service";
import type { PatientDbRecord } from "../../schemas/patient.schema";
import { IS_DEV, DEV_PATIENT_ID } from "../../constants";

// ─── Types ────────────────────────────────────────────────────────────────────
type DateRange = "7D" | "30D" | "90D" | "custom";

interface VitalPoint {
  date: string;        // "MMM D"
  isoDate: string;     // for filtering
  systolic?: number;
  diastolic?: number;
  heartRate?: number;
  temperature?: number;
  weight?: number;
  spo2?: number;
  bloodSugar?: number;
}

// ─── Cache structure ──────────────────────────────────────────────────────────
interface VitalsCache {
  patientId: string;
  data: VitalPoint[];
  savedAt: string;
}

const CACHE_KEY = (pid: string) => `vitals_trend_${pid}`;

function saveCache(patientId: string, data: VitalPoint[]) {
  try {
    const cache: VitalsCache = { patientId, data, savedAt: new Date().toISOString() };
    localStorage.setItem(CACHE_KEY(patientId), JSON.stringify(cache));
  } catch {}
}

function loadCache(patientId: string): VitalsCache | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY(patientId));
    if (raw) return JSON.parse(raw) as VitalsCache;
  } catch {}
  return null;
}

// ─── Map API response → VitalPoint ───────────────────────────────────────────
function mapApiPoint(p: VitalsTrendPoint): VitalPoint {
  const d = new Date(p.visit_date);
  return {
    date: d.toLocaleDateString("en-IN", { day: "numeric", month: "short" }),
    isoDate: d.toISOString().slice(0, 10),
    systolic:    p.blood_pressure_systolic,
    diastolic:   p.blood_pressure_diastolic,
    heartRate:   p.heart_rate,
    temperature: p.temperature != null ? Number(p.temperature) : undefined,
    weight:      p.weight != null ? Number(p.weight) : undefined,
    spo2:        p.oxygen_saturation,
    bloodSugar:  p.blood_sugar != null ? Number(p.blood_sugar) : undefined,
  };
}

// dataState drives what the page renders instead of a loading boolean.
// "live"            — API returned ≥1 vitals points (real data)
// "cache"           — API failed (offline), localStorage cache hit
// "no-vitals"       — API succeeded but patient has 0 vitals recorded yet
// "offline-no-cache"— API failed AND no prior cache; cannot show any data
type DataState = "loading" | "live" | "cache" | "no-vitals" | "offline-no-cache";

function formatSyncTime(iso: string | null): string {
  if (!iso) return "Unknown";
  const diffMin = Math.round((Date.now() - new Date(iso).getTime()) / 60_000);
  if (diffMin < 1)  return "just now";
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHrs = Math.round(diffMin / 60);
  if (diffHrs < 24) return `${diffHrs}h ago`;
  return `${Math.round(diffHrs / 24)}d ago`;
}

// ─── Normal ranges ────────────────────────────────────────────────────────────
const RANGES = {
  systolic:    { lo: 90,   hi: 140 },
  diastolic:   { lo: 60,   hi: 90  },
  heartRate:   { lo: 60,   hi: 100 },
  temperature: { lo: 96.8, hi: 99.5 },
  weight:      { lo: 50,   hi: 90  },
  spo2:        { lo: 95,   hi: 100 },
  bloodSugar:  { lo: 70,   hi: 110 },
};

function getStatus(val: number | undefined, key: keyof typeof RANGES) {
  if (val === undefined) return "normal";
  const { lo, hi } = RANGES[key];
  if (val > hi) return "high";
  if (val < lo) return "low";
  return "normal";
}

const STATUS_COLOR: Record<string, string> = {
  normal: "text-emerald-600 bg-emerald-50 border-emerald-200",
  high:   "text-red-600 bg-red-50 border-red-200",
  low:    "text-amber-600 bg-amber-50 border-amber-200",
};

// ─── Custom tooltip ───────────────────────────────────────────────────────────
const CustomTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-slate-100 shadow-lg rounded-xl px-3 py-2.5 text-xs">
      <p className="font-semibold text-slate-700 mb-1.5">{label}</p>
      {payload.map((p: any) => (
        <div key={p.dataKey} className="flex items-center gap-2 mb-0.5">
          <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: p.color }} />
          <span className="text-slate-500">{p.name}:</span>
          <span className="font-semibold text-slate-800">{p.value}</span>
        </div>
      ))}
    </div>
  );
};

// ─── Chart card ──────────────────────────────────────────────────────────────
interface ChartCardProps {
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
  badge?: { label: string; status: string };
  accent: string;
  empty?: boolean;
}

const ChartCard: React.FC<ChartCardProps> = ({ title, icon, children, badge, accent, empty }) => (
  <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5 flex flex-col gap-3">
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2">
        <div className={`w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0 ${accent}`}>
          {icon}
        </div>
        <span className="text-[15px] font-bold text-slate-800">{title}</span>
      </div>
      {badge && (
        <span className={`text-xs font-medium border rounded-full px-2.5 py-0.5 ${STATUS_COLOR[badge.status]}`}>
          {badge.label}
        </span>
      )}
    </div>
    <div className="h-48">
      {empty
        ? <div className="h-full flex items-center justify-center text-xs text-slate-400">No readings recorded yet.</div>
        : children}
    </div>
  </div>
);

// ─── Icons ────────────────────────────────────────────────────────────────────
const IcBP = () => (
  <svg className="w-4 h-4 text-cyan-600" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
    <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
  </svg>
);
const IcHeart = () => (
  <svg className="w-4 h-4 text-red-500" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
  </svg>
);
const IcThermo = () => (
  <svg className="w-4 h-4 text-orange-500" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
    <path d="M14 14.76V3.5a2.5 2.5 0 0 0-5 0v11.26a4.5 4.5 0 1 0 5 0z" />
  </svg>
);
const IcWeight = () => (
  <svg className="w-4 h-4 text-emerald-600" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
    <path d="m16 16 3-8 3 8c-.87.65-1.92 1-3 1s-2.13-.35-3-1Z" />
    <path d="m2 16 3-8 3 8c-.87.65-1.92 1-3 1s-2.13-.35-3-1Z" />
    <path d="M7 21h10M12 3v5M5 3h14" />
  </svg>
);
const IcDroplet = () => (
  <svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
    <path d="M12 2.69l5.66 5.66a8 8 0 1 1-11.31 0z" />
  </svg>
);
const IcSugar = () => (
  <svg className="w-4 h-4 text-pink-500" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
    <circle cx="12" cy="12" r="9" />
    <path d="M9 10a3 3 0 0 1 6 0c0 3-6 3-6 6a3 3 0 0 0 6 0" />
  </svg>
);
const IcDownload = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="7 10 12 15 17 10" />
    <line x1="12" y1="15" x2="12" y2="3" />
  </svg>
);

// ─── Main page ────────────────────────────────────────────────────────────────
const VitalsTrendPage: React.FC = () => {
  const [searchParams]              = useSearchParams();
  const patientId                   = searchParams.get("patient_id") ?? (IS_DEV ? DEV_PATIENT_ID : "");

  const [patient,    setPatient]    = useState<PatientDbRecord | null>(null);
  const [allData,    setAllData]    = useState<VitalPoint[]>([]);
  const [savedAt,    setSavedAt]    = useState<string | null>(null);
  const [dataState,  setDataState]  = useState<DataState>("loading");
  const [range,      setRange]      = useState<DateRange>("30D");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo,   setCustomTo]   = useState("");
  const [exporting,  setExporting]  = useState(false);
  const [isOnline,   setIsOnline]   = useState(() => navigator.onLine);
  const chartsRef                   = useRef<HTMLDivElement>(null);

  // Online/offline listener
  useEffect(() => {
    const goOnline  = () => setIsOnline(true);
    const goOffline = () => setIsOnline(false);
    window.addEventListener("online",  goOnline);
    window.addEventListener("offline", goOffline);
    return () => {
      window.removeEventListener("online",  goOnline);
      window.removeEventListener("offline", goOffline);
    };
  }, []);

  // Fetch patient info for header
  useEffect(() => {
    if (!patientId) return;
    PatientAPI.getById(patientId).then(setPatient).catch(() => {});
  }, [patientId]);

  // Fetch vitals from API; fall back to cache; never fabricate data.
  useEffect(() => {
    if (!patientId) { setDataState("no-vitals"); return; }

    setDataState("loading");

    DiagnosisAPI.getVitalsTrend(patientId, 90)
      .then((points) => {
        if (points.length === 0) {
          // Patient exists but has no vitals recorded yet — show empty state.
          setDataState("no-vitals");
          return;
        }
        const mapped = points
          .slice()
          .sort((a, b) => a.visit_date.localeCompare(b.visit_date))
          .map(mapApiPoint);
        setAllData(mapped);
        const ts = new Date().toISOString();
        setSavedAt(ts);
        saveCache(patientId, mapped);
        setDataState("live");
      })
      .catch(() => {
        // Offline or network error — use cache if available, otherwise block.
        const cached = loadCache(patientId);
        if (cached) {
          setAllData(cached.data);
          setSavedAt(cached.savedAt);
          setDataState("cache");
        } else {
          setDataState("offline-no-cache");
        }
      });
  }, [patientId]);

  // Filter by selected date range
  const filtered = useCallback((): VitalPoint[] => {
    const now = new Date();
    if (range === "7D")  { const cutoff = new Date(now); cutoff.setDate(cutoff.getDate() - 7);  return allData.filter(p => p.isoDate >= cutoff.toISOString().slice(0, 10)); }
    if (range === "30D") { const cutoff = new Date(now); cutoff.setDate(cutoff.getDate() - 30); return allData.filter(p => p.isoDate >= cutoff.toISOString().slice(0, 10)); }
    if (range === "90D") return allData;
    if (range === "custom" && customFrom && customTo) return allData.filter(p => p.isoDate >= customFrom && p.isoDate <= customTo);
    return allData;
  }, [allData, range, customFrom, customTo]);

  const data   = filtered();
  const latest = data[data.length - 1] ?? {};
  const tickInterval = data.length <= 10 ? 0 : data.length <= 30 ? 3 : 6;

  const handleExport = async () => {
    if (!chartsRef.current) return;
    setExporting(true);
    try {
      const canvas = await html2canvas(chartsRef.current, { backgroundColor: "#f5f5f7", scale: 2 });
      const url    = canvas.toDataURL("image/png");
      const a      = document.createElement("a");
      a.href       = url;
      a.download   = `vitals-trend-${new Date().toISOString().slice(0, 10)}.png`;
      a.click();
    } finally {
      setExporting(false);
    }
  };

  const RANGE_BTNS: DateRange[] = ["7D", "30D", "90D"];

  return (
    <div className="min-h-screen bg-[#f5f5f7]">
      <Sidebar />
      <div className="lg:pl-[var(--sidebar-w)] transition-[padding-left] duration-300 ease-in-out pt-14 lg:pt-0 h-screen flex flex-col">
      <AppHeader title="Vitals Trend" />
      <div className="p-3 flex-1 flex flex-col overflow-hidden">
      <PageContainer className="flex flex-col overflow-hidden flex-1">

        {/* ── Offline / cache banner ── */}
        {dataState === "cache" && (
          <div className="bg-amber-50 border-b border-amber-200 px-6 py-2 flex items-center gap-2 flex-shrink-0">
            <span className="w-2 h-2 rounded-full bg-amber-400 flex-shrink-0" />
            <p className="text-xs font-medium text-amber-700">
              Offline — showing cached data
              {savedAt && (
                <span className="font-normal text-amber-600"> · Last synced {formatSyncTime(savedAt)}</span>
              )}
            </p>
          </div>
        )}

        {/* ── Header ── */}
        <header className="bg-white border-b border-slate-100 px-6 py-4 flex-shrink-0">
          <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
            {/* Patient info */}
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-cyan-400 to-blue-500 flex items-center justify-center flex-shrink-0 text-white text-sm font-bold select-none">
                {patient
                  ? `${patient.first_name?.[0] ?? ""}${patient.last_name?.[0] ?? ""}`.toUpperCase()
                  : <svg viewBox="0 0 24 24" className="w-5 h-5 fill-white" aria-hidden="true"><circle cx="12" cy="8" r="4" /><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" /></svg>
                }
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <p className="text-sm font-bold text-slate-900">
                    {patient ? `${patient.first_name} ${patient.last_name}` : "Vitals Trend"}
                  </p>
                  {patient?.display_id && (
                    <span className="text-xs font-mono text-slate-400 bg-slate-100 rounded px-1.5 py-0.5">
                      {patient.display_id}
                    </span>
                  )}
                </div>
                <p className="text-xs text-slate-400">
                  Vitals trend
                  {savedAt && isOnline && (
                    <span className="ml-2 text-slate-300">· Synced {formatSyncTime(savedAt)}</span>
                  )}
                </p>
              </div>
            </div>

            {/* Controls */}
            <div className="flex items-center gap-2 flex-wrap justify-end">
              <div className="flex items-center bg-slate-100 rounded-xl p-1 gap-0.5">
                {RANGE_BTNS.map(r => (
                  <button
                    key={r}
                    onClick={() => setRange(r)}
                    className={`px-3.5 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                      range === r
                        ? "bg-white text-slate-900 shadow-sm"
                        : "text-slate-500 hover:text-slate-700"
                    }`}
                  >
                    {r}
                  </button>
                ))}
                <button
                  onClick={() => setRange("custom")}
                  className={`px-3.5 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                    range === "custom"
                      ? "bg-white text-slate-900 shadow-sm"
                      : "text-slate-500 hover:text-slate-700"
                  }`}
                >
                  Custom
                </button>
              </div>

              {range === "custom" && (
                <DateRangePicker
                  from={customFrom}
                  to={customTo}
                  onChange={(f, t) => { setCustomFrom(f); setCustomTo(t); }}
                />
              )}

              <button
                onClick={handleExport}
                disabled={exporting}
                className="flex items-center gap-1.5 text-xs font-semibold text-white px-4 py-2 rounded-xl disabled:opacity-60 transition-opacity"
                style={{ background: "linear-gradient(135deg, #22d3ee, #3b82f6)" }}
              >
                <IcDownload />
                {exporting ? "Exporting…" : "Export PNG"}
              </button>
            </div>
          </div>
        </header>

        {/* ── Loading state ── */}
        {dataState === "loading" && (
          <div className="flex-1 flex items-center justify-center">
            <p className="text-sm text-slate-400 animate-pulse">Loading vitals…</p>
          </div>
        )}

        {/* ── No vitals recorded yet ── */}
        {dataState === "no-vitals" && (
          <div className="flex-1 flex flex-col items-center justify-center gap-3 px-6 text-center">
            <div className="w-14 h-14 rounded-2xl bg-slate-100 flex items-center justify-center">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
              </svg>
            </div>
            <div>
              <p className="text-sm font-semibold text-slate-700">No vitals recorded yet</p>
              <p className="text-xs text-slate-400 mt-1">Vitals will appear here after the first visit where they are entered.</p>
            </div>
          </div>
        )}

        {/* ── Offline with no cache ── */}
        {dataState === "offline-no-cache" && (
          <div className="flex-1 flex flex-col items-center justify-center gap-3 px-6 text-center">
            <div className="w-14 h-14 rounded-2xl bg-amber-50 flex items-center justify-center">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#d97706" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <line x1="1" y1="1" x2="23" y2="23" />
                <path d="M16.72 11.06A10.94 10.94 0 0 1 19 12.55" />
                <path d="M5 12.55a10.94 10.94 0 0 1 5.17-2.39" />
                <path d="M10.71 5.05A16 16 0 0 1 22.56 9" />
                <path d="M1.42 9a15.91 15.91 0 0 1 4.7-2.88" />
                <path d="M8.53 16.11a6 6 0 0 1 6.95 0" />
                <line x1="12" y1="20" x2="12.01" y2="20" />
              </svg>
            </div>
            <div>
              <p className="text-sm font-semibold text-slate-700">No cached data available</p>
              <p className="text-xs text-slate-400 mt-1 max-w-xs">
                You're offline and this patient's vitals trend has never been loaded on this device.
                Reconnect to view the chart.
              </p>
            </div>
          </div>
        )}

        {/* ── Charts grid ── */}
        {(dataState === "live" || dataState === "cache") && (
          <div className="flex-1 overflow-y-auto">
            <div ref={chartsRef} className="p-5 grid grid-cols-1 lg:grid-cols-2 gap-4 pb-8">

              {/* Blood Pressure */}
              <ChartCard
                title="Blood Pressure"
                empty={data.length === 0}
                icon={<IcBP />}
                accent="bg-cyan-50"
                badge={{
                  label: `${latest.systolic ?? "—"}/${latest.diastolic ?? "—"} mmHg`,
                  status: getStatus(latest.systolic, "systolic"),
                }}
              >
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={data} margin={{ top: 4, right: 8, left: -16, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#94a3b8" }} interval={tickInterval} />
                    <YAxis tick={{ fontSize: 10, fill: "#94a3b8" }} domain={[50, 170]} />
                    <Tooltip content={<CustomTooltip />} />
                    <ReferenceArea y1={RANGES.systolic.lo} y2={RANGES.systolic.hi} fill="#dcfce7" fillOpacity={0.5} />
                    <Line dataKey="systolic" name="Systolic" stroke="#22d3ee" strokeWidth={2} dot={false} activeDot={{ r: 4 }} />
                    <Line dataKey="diastolic" name="Diastolic" stroke="#a78bfa" strokeWidth={2} dot={false} activeDot={{ r: 4 }} />
                    <Legend wrapperStyle={{ fontSize: 11, paddingTop: 4 }} />
                  </ComposedChart>
                </ResponsiveContainer>
              </ChartCard>

              {/* Heart Rate */}
              <ChartCard
                title="Heart Rate"
                empty={data.length === 0}
                icon={<IcHeart />}
                accent="bg-red-50"
                badge={{
                  label: `${latest.heartRate ?? "—"} bpm`,
                  status: getStatus(latest.heartRate, "heartRate"),
                }}
              >
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={data} margin={{ top: 4, right: 8, left: -16, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#94a3b8" }} interval={tickInterval} />
                    <YAxis tick={{ fontSize: 10, fill: "#94a3b8" }} domain={[40, 130]} />
                    <Tooltip content={<CustomTooltip />} />
                    <ReferenceArea y1={RANGES.heartRate.lo} y2={RANGES.heartRate.hi} fill="#dcfce7" fillOpacity={0.5} />
                    <Line dataKey="heartRate" name="Heart Rate" stroke="#f87171" strokeWidth={2} dot={false} activeDot={{ r: 4 }} />
                  </ComposedChart>
                </ResponsiveContainer>
              </ChartCard>

              {/* Temperature */}
              <ChartCard
                title="Temperature"
                empty={data.length === 0}
                icon={<IcThermo />}
                accent="bg-orange-50"
                badge={{
                  label: `${latest.temperature ?? "—"} °F`,
                  status: getStatus(latest.temperature, "temperature"),
                }}
              >
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={data} margin={{ top: 4, right: 8, left: -16, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#94a3b8" }} interval={tickInterval} />
                    <YAxis tick={{ fontSize: 10, fill: "#94a3b8" }} domain={[35, 40]} tickCount={6} />
                    <Tooltip content={<CustomTooltip />} />
                    <ReferenceArea y1={RANGES.temperature.lo} y2={RANGES.temperature.hi} fill="#dcfce7" fillOpacity={0.5} />
                    <Line dataKey="temperature" name="Temperature" stroke="#fb923c" strokeWidth={2} dot={false} activeDot={{ r: 4 }} />
                  </ComposedChart>
                </ResponsiveContainer>
              </ChartCard>

              {/* Weight */}
              <ChartCard
                title="Weight"
                empty={data.length === 0}
                icon={<IcWeight />}
                accent="bg-emerald-50"
                badge={{
                  label: `${latest.weight ?? "—"} kg`,
                  status: getStatus(latest.weight, "weight"),
                }}
              >
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={data} margin={{ top: 4, right: 8, left: -16, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#94a3b8" }} interval={tickInterval} />
                    <YAxis tick={{ fontSize: 10, fill: "#94a3b8" }} domain={["auto", "auto"]} />
                    <Tooltip content={<CustomTooltip />} />
                    <ReferenceArea y1={RANGES.weight.lo} y2={RANGES.weight.hi} fill="#dcfce7" fillOpacity={0.3} />
                    <Line dataKey="weight" name="Weight" stroke="#34d399" strokeWidth={2} dot={false} activeDot={{ r: 4 }} />
                  </ComposedChart>
                </ResponsiveContainer>
              </ChartCard>

              {/* SpO2 */}
              <ChartCard
                title="SpO2 (Oxygen Saturation)"
                empty={data.length === 0}
                icon={<IcDroplet />}
                accent="bg-blue-50"
                badge={{
                  label: `${latest.spo2 ?? "—"} %`,
                  status: getStatus(latest.spo2, "spo2"),
                }}
              >
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={data} margin={{ top: 4, right: 8, left: -16, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#94a3b8" }} interval={tickInterval} />
                    <YAxis tick={{ fontSize: 10, fill: "#94a3b8" }} domain={[88, 100]} tickCount={5} />
                    <Tooltip content={<CustomTooltip />} />
                    <ReferenceArea y1={RANGES.spo2.lo} y2={RANGES.spo2.hi} fill="#dcfce7" fillOpacity={0.5} />
                    <Line dataKey="spo2" name="SpO2" stroke="#60a5fa" strokeWidth={2} dot={false} activeDot={{ r: 4 }} />
                  </ComposedChart>
                </ResponsiveContainer>
              </ChartCard>

              {/* Blood Sugar */}
              <ChartCard
                title="Blood Sugar"
                empty={data.length === 0}
                icon={<IcSugar />}
                accent="bg-pink-50"
                badge={{
                  label: `${latest.bloodSugar ?? "—"} mg/dL`,
                  status: getStatus(latest.bloodSugar, "bloodSugar"),
                }}
              >
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={data} margin={{ top: 4, right: 8, left: -16, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#94a3b8" }} interval={tickInterval} />
                    <YAxis tick={{ fontSize: 10, fill: "#94a3b8" }} domain={[50, 150]} />
                    <Tooltip content={<CustomTooltip />} />
                    <ReferenceArea y1={RANGES.bloodSugar.lo} y2={RANGES.bloodSugar.hi} fill="#dcfce7" fillOpacity={0.5} />
                    <Line dataKey="bloodSugar" name="Blood Sugar" stroke="#f472b6" strokeWidth={2} dot={false} activeDot={{ r: 4 }} />
                  </ComposedChart>
                </ResponsiveContainer>
              </ChartCard>

            </div>
          </div>
        )}

      </PageContainer>
      </div>
      </div>
    </div>
  );
};

export default VitalsTrendPage;
